import { useState, useCallback, useRef } from "react";
import { Settings, Palette, RectangleHorizontal, Mic, Globe, Volume2, Loader2, Save, Play, Square, Gauge, Download, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAppSettings, LANGUAGES, type AccentColor, type ButtonShape, type VoiceOption, type SpeechRate } from "@/hooks/useAppSettings";
import { useAuth } from "@/hooks/useAuth";
import { useIsPremium } from "@/hooks/useIsPremium";
import { Link } from "react-router-dom";
import { Crown, Lock } from "lucide-react";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const accentColors: { value: AccentColor; label: string; swatch: string }[] = [
  { value: "red", label: "Rouge", swatch: "bg-red-500" },
  { value: "violet", label: "Violet", swatch: "bg-violet-500" },
  { value: "blue", label: "Bleu", swatch: "bg-blue-500" },
  { value: "orange", label: "Orange", swatch: "bg-orange-500" },
  { value: "emerald", label: "Émeraude", swatch: "bg-emerald-500" },
  { value: "yellow", label: "Jaune", swatch: "bg-yellow-400" },
  { value: "rose", label: "Rose", swatch: "bg-rose-500" },
  { value: "cyan", label: "Cyan", swatch: "bg-cyan-500" },
  { value: "purple", label: "Pourpre", swatch: "bg-purple-500" },
  { value: "amber", label: "Ambre", swatch: "bg-amber-500" },
  { value: "teal", label: "Sarcelle", swatch: "bg-teal-500" },
  { value: "indigo", label: "Indigo", swatch: "bg-indigo-500" },
  { value: "pink", label: "Pink", swatch: "bg-pink-500" },
  { value: "lime", label: "Lime", swatch: "bg-lime-500" },
  { value: "fuchsia", label: "Fuchsia", swatch: "bg-fuchsia-500" },
  { value: "sky", label: "Ciel", swatch: "bg-sky-500" },
];

const buttonShapes: { value: ButtonShape; label: string; preview: string }[] = [
  { value: "rounded", label: "Arrondi", preview: "rounded-xl" },
  { value: "square", label: "Carré", preview: "rounded-sm" },
];

type VoiceInfo = {
  value: VoiceOption;
  label: string;
  emoji: string;
  desc: string;
  category: "french" | "female" | "male";
};

const voices: VoiceInfo[] = [
  // 🇫🇷 Voix françaises (ElevenLabs pré-fabriquées)
  { value: "danick", label: "Danick", emoji: "🇫🇷", desc: "Voix clonée, chaleureuse", category: "french" },
  { value: "teddy", label: "Roger", emoji: "🇫🇷", desc: "Narrateur, posé", category: "french" },
  { value: "nicolas", label: "George", emoji: "🇫🇷", desc: "Profond, rassurant", category: "french" },
  { value: "claire", label: "Laura", emoji: "🇫🇷", desc: "Calme, assurée", category: "french" },
  { value: "anais", label: "Alice", emoji: "🇫🇷", desc: "Lumineuse, amicale", category: "french" },
  { value: "genevieve", label: "Matilda", emoji: "🇫🇷", desc: "Conteuse, professionnelle", category: "french" },
  { value: "antoine", label: "Liam", emoji: "🇫🇷", desc: "Jeune, énergique", category: "french" },
  { value: "louis", label: "Charlie", emoji: "🇫🇷", desc: "Décontracté, amical", category: "french" },
  { value: "paul", label: "Callum", emoji: "🇫🇷", desc: "Neutre, informatif", category: "french" },
  // Female voices (English)
  { value: "sarah", label: "Sarah", emoji: "👩‍🏫", desc: "Chaleureuse, expressive", category: "female" },
  { value: "lily", label: "Lily", emoji: "👧", desc: "Douce, jeune", category: "female" },
  { value: "alice", label: "Alice", emoji: "🙋‍♀️", desc: "Lumineuse, amicale", category: "female" },
  { value: "jessica", label: "Jessica", emoji: "👩‍💼", desc: "Claire, enjouée", category: "female" },
  { value: "laura", label: "Laura", emoji: "👩‍🔬", desc: "Calme, assurée", category: "female" },
  { value: "matilda", label: "Matilda", emoji: "📖", desc: "Conteuse pro", category: "female" },
  { value: "bella", label: "Bella", emoji: "✨", desc: "Pro, lumineuse", category: "female" },
  { value: "maria", label: "Maria", emoji: "🌸", desc: "Jeune, naturelle", category: "female" },
  { value: "river", label: "River", emoji: "🌊", desc: "Neutre, informative", category: "female" },
  // Male voices (English)
  { value: "liam", label: "Liam", emoji: "👦", desc: "Jeune, énergique", category: "male" },
  { value: "charlie", label: "Charlie", emoji: "😊", desc: "Décontracté, amical", category: "male" },
  { value: "george", label: "George", emoji: "👨‍🏫", desc: "Profond, rassurant", category: "male" },
  { value: "daniel", label: "Daniel", emoji: "🎓", desc: "Confiant, clair", category: "male" },
  { value: "brian", label: "Brian", emoji: "📢", desc: "Narrateur autoritaire", category: "male" },
  { value: "roger", label: "Roger", emoji: "🎙️", desc: "Baryton chaleureux", category: "male" },
  { value: "eric", label: "Eric", emoji: "🧑‍🎤", desc: "Doux, bienveillant", category: "male" },
  { value: "callum", label: "Callum", emoji: "🎤", desc: "Assuré, fluide", category: "male" },
  { value: "joseph", label: "Joseph", emoji: "🤝", desc: "Clair, amical", category: "male" },
  { value: "will", label: "Will", emoji: "🌟", desc: "Optimiste, relax", category: "male" },
  { value: "chris", label: "Chris", emoji: "👨", desc: "Charmant, naturel", category: "male" },
  { value: "harry", label: "Harry", emoji: "⚔️", desc: "Énergique, vif", category: "male" },
  { value: "adam", label: "Adam", emoji: "💼", desc: "Ferme, posé", category: "male" },
  { value: "bill", label: "Bill", emoji: "👴", desc: "Sage, équilibré", category: "male" },
];

const voiceSamples: Record<string, string> = {
  "fr-FR": "Salut ! Je suis ton assistant. Prêt à apprendre ensemble ?",
  "en-US": "Hi there! I'm your assistant. Ready to learn together?",
  "es-ES": "¡Hola! Soy tu asistente. ¿Listos para aprender juntos?",
  "de-DE": "Hallo! Ich bin dein Assistent. Bereit, gemeinsam zu lernen?",
  "pt-BR": "Olá! Sou seu assistente. Pronto para aprender juntos?",
  "ar-SA": "مرحبا! أنا مساعدك. هل أنت مستعد للتعلم معاً؟",
  "it-IT": "Ciao! Sono il tuo assistente. Pronti a imparare insieme?",
  default: "Hello! I'm your learning assistant. Let's get started!",
};

const TTS_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/elevenlabs-tts`;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const AppSettingsModal = ({ open, onOpenChange }: Props) => {
  const { t } = useLanguage();
  const {
    accentColor, setAccentColor,
    buttonShape, setButtonShape,
    voice, setVoice,
    language, setLanguage,
    voiceEnabled, setVoiceEnabled,
    speechRate, setSpeechRate,
    saveSettings, saving, dirty,
  } = useAppSettings();
  const { user } = useAuth();
  const { isPremium } = useIsPremium();
  const pwa = usePWAInstall();
  const [iosGuideOpen, setIosGuideOpen] = useState(false);
  const [playingVoice, setPlayingVoice] = useState<VoiceOption | null>(null);
  const [loadingVoice, setLoadingVoice] = useState<VoiceOption | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const previewVoice = useCallback(async (v: VoiceOption) => {
    // Stop current playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingVoice(null);

    const langPrefix = language.split("-")[0];
    const sampleText = voiceSamples[language] || voiceSamples[`${langPrefix}-${langPrefix.toUpperCase()}`] || voiceSamples["default"];

    setLoadingVoice(v);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error("Not authenticated");
      const response = await fetch(TTS_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text: sampleText, voice: v }),
      });

      if (!response.ok) {
        throw new Error("TTS failed");
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.onplay = () => { setPlayingVoice(v); setLoadingVoice(null); };
      audio.onended = () => { setPlayingVoice(null); URL.revokeObjectURL(audioUrl); audioRef.current = null; };
      audio.onerror = () => { setPlayingVoice(null); setLoadingVoice(null); URL.revokeObjectURL(audioUrl); audioRef.current = null; };

      await audio.play();
    } catch (err) {
      console.error("Voice preview error:", err);
      setLoadingVoice(null);
      setPlayingVoice(null);
    }
  }, [language]);

  const stopPreview = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingVoice(null);
    setLoadingVoice(null);
  }, []);

  const handleSave = async () => {
    await saveSettings();
    toast({
      title: "✅ Paramètres enregistrés",
      description: "Tes préférences ont été sauvegardées avec succès.",
    });
    onOpenChange(false);
  };

  const handleVoiceSelect = (v: VoiceOption) => {
    setVoice(v);
    previewVoice(v);
  };

  const frenchVoices = voices.filter((v) => v.category === "french");
  const femaleVoices = voices.filter((v) => v.category === "female");
  const maleVoices = voices.filter((v) => v.category === "male");

  return (
    <Dialog open={open} onOpenChange={(o) => {
      if (!o) stopPreview();
      onOpenChange(o);
    }}>
      <DialogContent className="sm:max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Settings className="w-5 h-5 text-primary" />
            {t("settings.title")}
          </DialogTitle>
          <DialogDescription>{t("settings.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pt-2">
          {/* Language */}
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-primary" />
              <Label className="text-sm font-bold">{t("settings.language")}</Label>
            </div>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border-2 border-border bg-background text-sm font-semibold focus:border-primary focus:outline-none transition-colors"
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
          </section>

          {/* Install PWA */}
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Download className="w-4 h-4 text-primary" />
              <Label className="text-sm font-bold">Application KelasAI</Label>
            </div>
            {pwa.installed ? (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 className="w-4 h-4" />
                KelasAI est installée sur cet appareil
              </div>
            ) : (
              <Button
                type="button"
                onClick={async () => {
                  if (pwa.canPrompt) await pwa.promptInstall("settings");
                  else setIosGuideOpen(true);
                }}
                className="w-full gap-2 font-bold"
                variant="outline"
              >
                <Download className="w-4 h-4" />
                Installer KelasAI sur cet appareil
              </Button>
            )}
          </section>

          <Dialog open={iosGuideOpen} onOpenChange={setIosGuideOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5 text-primary" /> Installer KelasAI
                </DialogTitle>
                <DialogDescription>
                  {pwa.isIOSLike
                    ? "Sur iPhone/iPad, l'installation se fait depuis Safari en 2 étapes."
                    : "Ouvre le menu de ton navigateur puis choisis « Installer l'application » ou « Ajouter à l'écran d'accueil »."}
                </DialogDescription>
              </DialogHeader>
              {pwa.isIOSLike && (
                <ol className="space-y-3 py-2 text-sm">
                  <li>1. Appuie sur le bouton <strong>Partager</strong> de Safari.</li>
                  <li>2. Choisis <strong>« Sur l'écran d'accueil »</strong>, puis <strong>Ajouter</strong>.</li>
                </ol>
              )}
              <Button onClick={() => setIosGuideOpen(false)} className="w-full font-bold">OK</Button>
            </DialogContent>
          </Dialog>

          {/* Accent Color */}
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-primary" />
              <Label className="text-sm font-bold">Couleur d'accentuation</Label>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {accentColors.map((c) => (
                <button
                  key={c.value}
                  onClick={() => setAccentColor(c.value)}
                  className={cn(
                    "flex items-center gap-2 px-2.5 py-2 rounded-xl border-2 transition-all duration-200 text-xs font-semibold",
                    accentColor === c.value
                      ? "border-primary bg-primary/10 scale-[1.02]"
                      : "border-border hover:border-primary/40 hover:bg-muted"
                  )}
                >
                  <span className={cn("w-3.5 h-3.5 rounded-full shrink-0", c.swatch)} />
                  {c.label}
                </button>
              ))}
            </div>
          </section>

          {/* Button Shape */}
          <section className="space-y-3">
            <div className="flex items-center gap-2">
              <RectangleHorizontal className="w-4 h-4 text-primary" />
              <Label className="text-sm font-bold">Forme des boutons</Label>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {buttonShapes.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setButtonShape(s.value)}
                  className={cn(
                    "flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 transition-all duration-200",
                    buttonShape === s.value
                      ? "border-primary bg-primary/10 scale-[1.02]"
                      : "border-border hover:border-primary/40 hover:bg-muted"
                  )}
                >
                  <div className={cn("w-16 h-7 bg-primary", s.preview)} />
                  <span className="text-xs font-semibold">{s.label}</span>
                </button>
              ))}
            </div>
          </section>

          {/* Voice */}
          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Mic className="w-4 h-4 text-primary" />
                <Label className="text-sm font-bold">Assistant vocal</Label>
                {!isPremium && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-bold">
                    <Crown className="w-3 h-3" /> Premium
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{isPremium ? (voiceEnabled ? "Activé" : "Désactivé") : "Verrouillé"}</span>
                <Switch
                  checked={isPremium && voiceEnabled}
                  onCheckedChange={setVoiceEnabled}
                  disabled={!isPremium}
                />
              </div>
            </div>
            {!isPremium && (
              <Link
                to="/tarifs"
                onClick={() => onOpenChange(false)}
                className="flex items-center justify-between gap-3 p-3 rounded-2xl border-2 border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-primary" />
                  <span className="text-xs font-bold text-foreground">L'assistant vocal est réservé aux comptes Premium.</span>
                </div>
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                  <Crown className="w-3.5 h-3.5" /> Passer Premium
                </span>
              </Link>
            )}
            {isPremium && voiceEnabled && (
              <div className="space-y-4">
                <p className="text-xs text-muted-foreground">Clique sur ▶ pour écouter une voix avant de choisir.</p>

                {/* French voices */}
                <div className="space-y-2">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">🇫🇷 Voix françaises</p>
                  <RadioGroup value={voice} onValueChange={(v) => handleVoiceSelect(v as VoiceOption)} className="grid grid-cols-2 gap-2">
                    {frenchVoices.map((v) => (
                      <VoiceCard
                        key={v.value}
                        voice={v}
                        isSelected={voice === v.value}
                        isPlaying={playingVoice === v.value}
                        isLoading={loadingVoice === v.value}
                        onPreview={() => previewVoice(v.value)}
                        onStop={stopPreview}
                      />
                    ))}
                  </RadioGroup>
                </div>

                {/* Female voices */}
                <div className="space-y-2">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">👩 Voix féminines</p>
                  <RadioGroup value={voice} onValueChange={(v) => handleVoiceSelect(v as VoiceOption)} className="grid grid-cols-2 gap-2">
                    {femaleVoices.map((v) => (
                      <VoiceCard
                        key={v.value}
                        voice={v}
                        isSelected={voice === v.value}
                        isPlaying={playingVoice === v.value}
                        isLoading={loadingVoice === v.value}
                        onPreview={() => previewVoice(v.value)}
                        onStop={stopPreview}
                      />
                    ))}
                  </RadioGroup>
                </div>

                {/* Male voices */}
                <div className="space-y-2">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">👨 Voix masculines</p>
                  <RadioGroup value={voice} onValueChange={(v) => handleVoiceSelect(v as VoiceOption)} className="grid grid-cols-2 gap-2">
                    {maleVoices.map((v) => (
                      <VoiceCard
                        key={v.value}
                        voice={v}
                        isSelected={voice === v.value}
                        isPlaying={playingVoice === v.value}
                        isLoading={loadingVoice === v.value}
                        onPreview={() => previewVoice(v.value)}
                        onStop={stopPreview}
                      />
                    ))}
                  </RadioGroup>
                </div>

                {/* Speed preset */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Gauge className="w-3.5 h-3.5 text-primary" />
                    <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Vitesse de lecture</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {([
                      { v: "slow", label: "Lent", emoji: "🐢" },
                      { v: "normal", label: "Normal", emoji: "🚶" },
                      { v: "fast", label: "Rapide", emoji: "⚡" },
                    ] as { v: SpeechRate; label: string; emoji: string }[]).map((s) => (
                      <button
                        key={s.v}
                        type="button"
                        onClick={() => setSpeechRate(s.v)}
                        className={cn(
                          "px-2.5 py-2 rounded-xl border-2 text-xs font-bold transition-all duration-200",
                          speechRate === s.v
                            ? "border-primary bg-primary/10 scale-[1.02]"
                            : "border-border hover:border-primary/40 hover:bg-muted"
                        )}
                      >
                        <span className="mr-1">{s.emoji}</span>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 text-sm font-bold py-3"
            >
              Annuler
            </Button>
            {user && (
              <Button
                onClick={handleSave}
                disabled={saving || !dirty}
                className="flex-1 gap-2 text-sm font-bold py-3"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {saving ? "Enregistrement..." : dirty ? "Enregistrer" : "À jour ✓"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

function VoiceCard({ voice, isSelected, isPlaying, isLoading, onPreview, onStop }: {
  voice: VoiceInfo;
  isSelected: boolean;
  isPlaying: boolean;
  isLoading: boolean;
  onPreview: () => void;
  onStop: () => void;
}) {
  return (
    <label
      className={cn(
        "flex items-center gap-2 px-2.5 py-2.5 rounded-xl border-2 cursor-pointer transition-all duration-200 relative",
        isSelected
          ? "border-primary bg-primary/10 scale-[1.02]"
          : "border-border hover:border-primary/40 hover:bg-muted"
      )}
    >
      <RadioGroupItem value={voice.value} id={`voice-${voice.value}`} className="sr-only" />
      <div className="flex flex-col flex-1 min-w-0">
        <span className="text-sm font-bold truncate">
          {voice.emoji} {voice.label}
        </span>
        <span className="text-[10px] text-muted-foreground truncate">{voice.desc}</span>
      </div>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); isPlaying ? onStop() : onPreview(); }}
        className={cn(
          "shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all",
          isPlaying
            ? "bg-primary text-primary-foreground"
            : "bg-muted hover:bg-primary/20 text-muted-foreground hover:text-primary"
        )}
      >
        {isLoading ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : isPlaying ? (
          <Square className="w-3 h-3 fill-current" />
        ) : (
          <Play className="w-3.5 h-3.5 fill-current ml-0.5" />
        )}
      </button>
      {isPlaying && (
        <div className="absolute -top-1 -right-1 flex gap-[2px]">
          {[1, 2, 3].map((i) => (
            <span key={i} className="w-[3px] rounded-full bg-primary animate-pulse" style={{ height: `${8 + i * 3}px`, animationDelay: `${i * 0.15}s` }} />
          ))}
        </div>
      )}
    </label>
  );
}

export default AppSettingsModal;
